/**
 * Auth store — single source of truth for the signed-in member + family.
 *
 * Probe deduplication: `getSession()` shares one in-flight `/api/auth/me`
 * promise across concurrent callers so we never fan out duplicate probes.
 */

import { createSignal, createMemo, type Accessor } from 'solid-js';
import type { Member, Family } from '@shared/types';

export interface SignedIn {
  authenticated: true;
  member: Member;
  family: Family;
}
export interface Anonymous {
  authenticated: false;
  // Set when the email is magic-link-verified but no family exists yet —
  // the app routes these users to /onboarding to create their family.
  pendingEmail?: string;
}
export type SessionState = SignedIn | Anonymous;

const [session, setSession] = createSignal<SessionState | null>(null); // null = not probed
let inflight: Promise<SessionState> | null = null;

export async function getSession(opts: { force?: boolean } = {}): Promise<SessionState> {
  if (opts.force) { inflight = null; setSession(null); }
  const current = session();
  if (current && !opts.force) return current;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const r = await fetch('/api/auth/me', { credentials: 'same-origin' });
      if (!r.ok) { const s: Anonymous = { authenticated: false }; setSession(s); return s; }
      const data = (await r.json()) as { member?: Member; family?: Family; pendingEmail?: string };
      const s: SessionState = data.member && data.family
        ? { authenticated: true, member: data.member, family: data.family }
        : { authenticated: false, ...(data.pendingEmail ? { pendingEmail: data.pendingEmail } : {}) };
      setSession(s);
      return s;
    } catch {
      const s: Anonymous = { authenticated: false };
      setSession(s);
      return s;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export async function signOut(): Promise<void> {
  try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }); } catch {}
  setSession({ authenticated: false });
  inflight = null;
}

export const sessionSignal: Accessor<SessionState | null> = session;
export const isSignedIn: Accessor<boolean> = createMemo(() => session()?.authenticated === true);
export const pendingEmail: Accessor<string | null> = createMemo(() => {
  const s = session();
  return s && !s.authenticated ? (s.pendingEmail ?? null) : null;
});
export const currentMember: Accessor<Member | null> = createMemo(() => {
  const s = session();
  return s?.authenticated ? s.member : null;
});
export const currentFamily: Accessor<Family | null> = createMemo(() => {
  const s = session();
  return s?.authenticated ? s.family : null;
});
export const isOwner: Accessor<boolean> = createMemo(() => {
  const s = session();
  return s?.authenticated ? s.member.role === 'owner' : false;
});
